import axiosInstance, { endpoints } from "src/lib/axios";
import { StyrelsemöteDto, StyrelseProtokollDto, ÄmneFörStyrelsemöteDto, UppdateraStyrelsemöteRequest, StyrelseledamotDto } from "src/types/styrelse";
import { medOrgHuvud } from 'src/lib/org-huvud';

export function hämtaNästaStyrelsemöte(orgId: string): Promise<StyrelsemöteDto> {
    return axiosInstance.get(endpoints.styrelse.möte.nästa(orgId))
        .then(response => response.data);
}

export function hämtaStyrelsemöte(moteId: string): Promise<StyrelsemöteDto> {
    return axiosInstance.get(endpoints.styrelse.möte.hämta(moteId))
        .then(response => response.data);
}

export function uppdateraStyrelsemöte(moteId: string, request: UppdateraStyrelsemöteRequest): Promise<StyrelsemöteDto> {
    return axiosInstance.put(endpoints.styrelse.möte.uppdatera(moteId), request)
        .then(response => response.data);
}

export function läggTillÄmne(moteId: string, ämne: ÄmneFörStyrelsemöteDto): Promise<StyrelsemöteDto> {
    return axiosInstance.post(endpoints.styrelse.möte.läggTillÄmne(moteId), ämne)
        .then(response => response.data);
}

export function uppdateraProtokoll(moteId: string, protokoll: StyrelseProtokollDto): Promise<StyrelsemöteDto> {
    return axiosInstance.put(endpoints.styrelse.möte.uppdateraProtokoll(moteId), protokoll)
        .then(response => response.data);
}

export function läggTillProtokollPunkt(moteId: string, punkt: { rubrik: string; stycken: string[] }): Promise<StyrelsemöteDto> {
    return axiosInstance.post(endpoints.styrelse.möte.läggTillProtokollPunkt(moteId), punkt)
        .then(response => response.data);
}

export function hämtaProtokollPdf(moteId: string): Promise<Blob> {
    return axiosInstance
        .get(endpoints.styrelse.möte.protokollPdf(moteId), { responseType: 'blob' })
        .then((response) => response.data);
}

export function startaStyrelsemöte(moteId: string): Promise<StyrelsemöteDto> {
    return axiosInstance.post(endpoints.styrelse.möte.starta(moteId))
        .then(response => response.data);
}

export function avslutaStyrelsemöte(moteId: string): Promise<StyrelsemöteDto> {
    return axiosInstance.post(endpoints.styrelse.möte.avsluta(moteId))
        .then(response => response.data);
}

export function hämtaArkiv(orgId: string, query?: string): Promise<StyrelsemöteDto[]> {
    return axiosInstance
        .get(endpoints.styrelse.möte.arkiv(orgId), { params: query ? { query } : undefined, ...medOrgHuvud(orgId) })
        .then(response => response.data);
}

export function exporteraProtokollTillGoogleDocs(moteId: string): Promise<{ url: string }> {
    return axiosInstance.post(endpoints.styrelse.möte.googleDocs(moteId))
        .then(response => response.data);
}

export function delaProtokollUtkast(moteId: string, frontendUrl?: string): Promise<StyrelsemöteDto> {
    return axiosInstance.post(endpoints.styrelse.möte.delaUtkast(moteId), null, {
        params: frontendUrl ? { frontendUrl } : undefined,
    }).then(response => response.data);
}

export function importeraMötesTranskription(moteId: string): Promise<StyrelsemöteDto> {
    return axiosInstance.post(endpoints.styrelse.möte.importeraTranskription(moteId))
        .then(response => response.data);
}

export function sparaMötesTranskription(moteId: string, rader: string[]): Promise<StyrelsemöteDto> {
    return axiosInstance.post(endpoints.styrelse.möte.sparaTranskription(moteId), rader)
        .then(response => response.data);
}

export function formateraUrl(url: string): string {
    const trimmed = url.trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith('//')) {
        return trimmed;
    }
    return `https://${trimmed}`;
}

export function hämtaStyrelseledamöter(orgId: string): Promise<StyrelseledamotDto[]> {
    return axiosInstance.get(endpoints.styrelse.ledamot.lista(orgId))
        .then(response => response.data);
}

export function skapaStyrelseledamot(orgId: string, ledamot: Omit<StyrelseledamotDto, 'id'>): Promise<StyrelseledamotDto> {
    return axiosInstance.post(endpoints.styrelse.ledamot.skapa(orgId), ledamot)
        .then(response => response.data);
}

export function uppdateraStyrelseledamot(ledamotId: string, ledamot: Partial<StyrelseledamotDto>): Promise<StyrelseledamotDto> {
    return axiosInstance.put(endpoints.styrelse.ledamot.uppdatera(ledamotId), ledamot)
        .then(response => response.data);
}

export function raderaStyrelseledamot(ledamotId: string): Promise<void> {
    return axiosInstance.delete(endpoints.styrelse.ledamot.radera(ledamotId))
        .then(() => undefined);
}

